import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  collection, onSnapshot, addDoc, serverTimestamp, updateDoc,
  query, orderBy, doc, getDoc, getDocs, where, arrayUnion,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import {
  Plus, Minus, X, Search, ShoppingCart, Trash2,
  ChevronRight, Users, Receipt, Clock, CheckCircle2,
  MessageSquare, ChefHat, Utensils, SplitSquareVertical,
} from 'lucide-react';
import { useParams, useSearchParams } from 'react-router-dom';
import ModalOpciones, { ModalEligeCombo } from './ModalOpciones';
import { tieneOpciones, nombreConOpciones, idLineaCarrito, precioDesde } from '../../utils/opcionesHelper';
import { ordenarCategorias, ordenarCatalogo } from '../../utils/ordenCategorias';
import { useOrdenCategorias } from '../../hooks/useOrdenCategorias';

const fmt = (n) => new Intl.NumberFormat('es-CL', { minimumFractionDigits: 0 }).format(n || 0);
const esComboElige = (item) => item._tipo === 'combo' && item.componentes?.some(c => c.tipo === 'elige');

export default function CartaDigital() {
  const { mesaId } = useParams();
  // Código impreso en el QR de la mesa (?t=...). Sin él la carta del local no abre.
  const [params] = useSearchParams();
  const qrToken = params.get('t') || '';
  const [restaurante, setRestaurante] = useState({ nombre: 'Restaurante' });
  const [recetas, setRecetas] = useState([]);
  const [productos, setProductos] = useState([]);
  const [combos, setCombos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [mesaInfo, setMesaInfo] = useState(null);
  const [sesion, setSesion] = useState(null);
  const [sesionId, setSesionId] = useState(null);
  const [nombreComensal, setNombreComensal] = useState('');
  const [comensalRegistrado, setComensalRegistrado] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [catFiltro, setCatFiltro] = useState('');
  const [carrito, setCarrito] = useState([]);
  const [drawerAbierto, setDrawerAbierto] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [pedidoOk, setPedidoOk] = useState(false);
  const [verCuenta, setVerCuenta] = useState(false);
  const [modalDividir, setModalDividir] = useState(false);
  const [partesDividir, setPartesDividir] = useState(2);
  const ordenCats = useOrdenCategorias();

  const PUBLIC_API = (import.meta.env.VITE_BACKEND_URL ||
    'https://southamerica-east1-cocina-19123.cloudfunctions.net/api');

  // Cargar config restaurante vía endpoint público sanitizado
  useEffect(() => {
    fetch(`${PUBLIC_API}/api/public/config`)
      .then(r => r.json())
      .then(d => { if (d?.ok && d.config) setRestaurante(d.config); })
      .catch(() => {});
  }, []);

  // Cargar mesa info vía endpoint público sanitizado
  useEffect(() => {
    if (!mesaId) return;
    fetch(`${PUBLIC_API}/api/public/mesa/${encodeURIComponent(mesaId)}?t=${encodeURIComponent(qrToken)}`)
      .then(r => r.json())
      .then(d => { if (d?.ok && d.mesa) setMesaInfo(d.mesa); })
      .catch(() => {});
  }, [mesaId, qrToken]);

  // Buscar o crear sesión activa para esta mesa
  useEffect(() => {
    if (!mesaId) return;
    const q = query(
      collection(db, 'mesa_sesiones'),
      where('mesaId', '==', mesaId),
      where('estado', '==', 'abierta')
    );
    const unsub = onSnapshot(q, snap => {
      if (!snap.empty) {
        const s = snap.docs[0];
        setSesion({ id: s.id, ...s.data() });
        setSesionId(s.id);
      } else {
        setSesion(null);
        setSesionId(null);
      }
    });
    return () => unsub();
  }, [mesaId]);

  // Cargar catálogo vía endpoint público sanitizado
  useEffect(() => {
    let cancelado = false;
    const cargar = async () => {
      try {
        // canal=carta: la carta del local, distinta de la del menú web
        const resp = await fetch(`${PUBLIC_API}/api/public/menu?canal=carta` +
          `&mesa=${encodeURIComponent(mesaId || '')}&t=${encodeURIComponent(qrToken)}`);
        const data = await resp.json();
        if (cancelado || !data?.ok) return;
        setRecetas(data.recetas || []);
        setProductos(data.productos || []);
        setCombos(data.combos || []);
      } catch (err) {
        console.error('Error cargando menú:', err);
      } finally {
        if (!cancelado) setCargando(false);
      }
    };
    cargar();
    const interval = setInterval(cargar, 60000);
    return () => { cancelado = true; clearInterval(interval); };
  }, [mesaId, qrToken]);

  const catalogo = useMemo(() => ordenarCatalogo([
    ...recetas.map(r => ({ ...r, _tipo: 'receta', precioVenta: r.precioVenta ?? r.precioSugeridoVenta ?? 0 })),
    ...productos.map(p => ({ ...p, _tipo: 'producto', categoria: p.categoria || 'productos', precioVenta: p.precioVenta || p.precioVentaDirecto || 0 })),
    ...combos.map(c => ({ ...c, _tipo: 'combo', categoria: 'combos', precioVenta: c.precio || 0 })),
  ], ordenCats), [recetas, productos, combos, ordenCats]);

  const categorias = useMemo(
    () => ordenarCategorias([...new Set(catalogo.map(i => i.categoria))].filter(Boolean), ordenCats),
    [catalogo, ordenCats]);

  const catalogoFiltrado = useMemo(() =>
    catalogo.filter(i => {
      const b = i.nombre.toLowerCase().includes(busqueda.toLowerCase());
      const c = !catFiltro || i.categoria === catFiltro;
      return b && c && i.precioVenta > 0;
    }), [catalogo, busqueda, catFiltro]);

  const totalCarrito = useMemo(() => carrito.reduce((s, i) => s + i.subtotal, 0), [carrito]);
  const cantCarrito = useMemo(() => carrito.reduce((s, i) => s + i.cantidad, 0), [carrito]);

  const [errorStock, setErrorStock] = useState('');
  const [itemOpciones, setItemOpciones] = useState(null);
  const [comboEligiendo, setComboEligiendo] = useState(null);

  const agregarItem = useCallback((item) => {
    // Bloquear si el item no está disponible
    if (item.disponible === false) {
      setErrorStock(`${item.nombre} no está disponible`);
      setTimeout(() => setErrorStock(''), 3000);
      return;
    }
    // Para productos con stock bajo (cantidadActual sale del endpoint sólo si es < 5)
    if (item._tipo === 'producto' && !item.stockInfinito && typeof item.cantidadActual === 'number') {
      const enCarrito = carrito.find(i => i.id === item.id)?.cantidad || 0;
      if (enCarrito + 1 > item.cantidadActual) {
        setErrorStock(`Solo quedan ${item.cantidadActual} de ${item.nombre}`);
        setTimeout(() => setErrorStock(''), 3000);
        return;
      }
    }
    // Tamaños o agregados: el comensal elige antes de sumarlo.
    // Sin esto el pedido viajaba sin tamaño y el servidor lo rechazaba, porque
    // el precio de una pizza depende del tamaño elegido.
    if (tieneOpciones(item)) {
      setItemOpciones(item);
      return;
    }
    // Combo con componentes a elegir (qué pizza, qué bebida): mismo modal que el menú web
    if (esComboElige(item)) {
      setComboEligiendo(item);
      return;
    }

    setCarrito(prev => {
      const idx = prev.findIndex(i => i.id === item.id);
      if (idx >= 0) {
        const n = [...prev];
        n[idx] = { ...n[idx], cantidad: n[idx].cantidad + 1, subtotal: (n[idx].cantidad + 1) * n[idx].precioVenta };
        return n;
      }
      return [...prev, { id: item.id, nombre: item.nombre, _tipo: item._tipo, cantidad: 1, precioVenta: item.precioVenta, subtotal: item.precioVenta, nota: '' }];
    });
  }, [carrito]);

  // Confirmación del modal de tamaños/agregados
  const confirmarOpciones = useCallback(({ variante, agregados, precioUnitario }) => {
    if (!itemOpciones) return;
    const base = itemOpciones;
    const lineaId = idLineaCarrito(base.id, variante, agregados);
    setCarrito(prev => {
      const idx = prev.findIndex(i => i.id === lineaId);
      if (idx >= 0) {
        const n = [...prev];
        n[idx] = { ...n[idx], cantidad: n[idx].cantidad + 1, subtotal: (n[idx].cantidad + 1) * n[idx].precioVenta };
        return n;
      }
      return [...prev, {
        id: lineaId,
        baseId: base.id,
        nombre: nombreConOpciones(base.nombre, variante, agregados),
        _tipo: base._tipo,
        variante: variante || null,
        agregados,
        cantidad: 1,
        precioVenta: precioUnitario,
        subtotal: precioUnitario,
        nota: '',
      }];
    });
    setItemOpciones(null);
  }, [itemOpciones]);

  // Confirmación del combo: lo elegido viaja al servidor para que cocina vea qué pizza y qué bebida
  const confirmarComboElige = useCallback((selecciones) => {
    if (!comboEligiendo) return;
    const combo = comboEligiendo;
    const elegidos = Object.values(selecciones).filter(Boolean);
    const lineaId = `combo_${combo.id}_${Object.entries(selecciones).map(([k, v]) => `${k}:${v?.id}`).join('|')}`;
    setCarrito(prev => {
      const idx = prev.findIndex(i => i.id === lineaId);
      if (idx >= 0) {
        const n = [...prev];
        n[idx] = { ...n[idx], cantidad: n[idx].cantidad + 1, subtotal: (n[idx].cantidad + 1) * n[idx].precioVenta };
        return n;
      }
      return [...prev, {
        id: lineaId,
        baseId: combo.id,
        nombre: elegidos.length ? `${combo.nombre} (${elegidos.map(s => s.nombre).join(' + ')})` : combo.nombre,
        _tipo: 'combo',
        selecciones: elegidos.map(s => ({ id: s.id, nombre: s.nombre })),
        cantidad: 1,
        precioVenta: combo.precioVenta,
        subtotal: combo.precioVenta,
        nota: '',
      }];
    });
    setComboEligiendo(null);
  }, [comboEligiendo]);

  const restarItem = useCallback((id) => {
    setCarrito(prev => {
      const idx = prev.findIndex(i => i.id === id);
      if (idx < 0) return prev;
      if (prev[idx].cantidad <= 1) return prev.filter(i => i.id !== id);
      const n = [...prev];
      n[idx] = { ...n[idx], cantidad: n[idx].cantidad - 1, subtotal: (n[idx].cantidad - 1) * n[idx].precioVenta };
      return n;
    });
  }, []);

  const iniciarSesion = async () => {
    if (!nombreComensal.trim()) return;
    try {
      if (!sesionId) {
        const ref = await addDoc(collection(db, 'mesa_sesiones'), {
          mesaId, mesaNumero: mesaInfo?.numero || 0,
          estado: 'abierta', comensales: [nombreComensal.trim()],
          pedidos: [], totalAcumulado: 0,
          fechaApertura: serverTimestamp(),
        });
        setSesionId(ref.id);
      } else {
        await updateDoc(doc(db, 'mesa_sesiones', sesionId), {
          comensales: arrayUnion(nombreComensal.trim()),
        });
      }
      setComensalRegistrado(true);
    } catch (e) {
      console.error(e);
    }
  };

  const enviarPedido = async () => {
    if (carrito.length === 0 || !sesionId) return;

    // Re-validar disponibilidad contra el catálogo actual antes de enviar
    for (const it of carrito) {
      if (it._tipo === 'producto') {
        const prod = productos.find(p => p.id === it.id);
        if (!prod || prod.disponible === false) {
          setErrorStock(`${it.nombre} ya no está disponible`);
          setTimeout(() => setErrorStock(''), 4000);
          return;
        }
        if (!prod.stockInfinito && typeof prod.cantidadActual === 'number' && prod.cantidadActual < it.cantidad) {
          setErrorStock(`Sin stock suficiente de ${it.nombre}. Disponible: ${prod.cantidadActual}`);
          setTimeout(() => setErrorStock(''), 4000);
          return;
        }
      }
    }

    setEnviando(true);
    try {
      // El pedido y el acumulado de la sesión los crea el servidor:
      // acá solo va QUÉ se pide. Los precios se recalculan contra la base.
      const resp = await fetch(`${PUBLIC_API}/api/public/pedido`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          canal: 'mesa',
          cliente: { nombre: nombreComensal },
          tipoPedido: 'mesa',
          mesa: String(mesaInfo?.numero || ''),
          mesaId,
          qrToken,
          sesionMesaId: sesionId,
          notas: `Pedido QR de ${nombreComensal}`,
          items: carrito.map(i => ({
            id: i.baseId || i.id,
            tipo: i._tipo,
            cantidad: i.cantidad,
            varianteId: i.variante?.id || null,
            agregadoIds: (i.agregados || []).map(a => a.id),
            selecciones: i.selecciones || [],
            nota: i.nota || '',
            nombre: i.nombre,
          })),
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.ok) {
        setErrorStock(data.error || 'No pudimos enviar el pedido. Intenta nuevamente.');
        setTimeout(() => setErrorStock(''), 5000);
        setEnviando(false);
        return;
      }

      setCarrito([]);
      setPedidoOk(true);
      setDrawerAbierto(false);
      setTimeout(() => setPedidoOk(false), 4000);
    } catch (e) {
      console.error(e);
    } finally {
      setEnviando(false);
    }
  };

  // ── PANTALLA: QR inválido, mesa eliminada o enlace sin el código del QR ──
  if (!cargando && !mesaInfo) return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center p-6">
      <div className="text-center text-white space-y-4">
        <Utensils size={64} className="mx-auto opacity-50" />
        <h1 className="text-2xl font-bold">Carta no disponible</h1>
        <p className="text-gray-400">Escanea el código QR de tu mesa para ver la carta. Si ya lo hiciste, pídele ayuda a un mesero.</p>
      </div>
    </div>
  );

  // ── PANTALLA: Registro del comensal ──
  if (!comensalRegistrado) return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a1a2e] to-[#16213e] flex items-center justify-center p-6">
      <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-sm space-y-6 text-center">
        <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto">
          <Utensils size={32} className="text-[#D32F2F]" />
        </div>
        <div>
          <h1 className="text-2xl font-black text-gray-900">{restaurante.nombre}</h1>
          <p className="text-gray-500 text-sm mt-1">Mesa {mesaInfo?.numero || '...'}</p>
        </div>
        <div className="space-y-2 text-left">
          <label className="text-xs font-bold text-gray-600 uppercase">Tu nombre</label>
          <input
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-2xl text-sm font-medium focus:outline-none focus:border-[#D32F2F]"
            placeholder="¿Cómo te llamas?"
            value={nombreComensal}
            onChange={e => setNombreComensal(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && iniciarSesion()}
          />
        </div>
        <button
          onClick={iniciarSesion}
          disabled={!nombreComensal.trim()}
          className="w-full py-3.5 bg-[#D32F2F] hover:bg-[#B71C1C] disabled:bg-gray-300 text-white rounded-2xl font-bold text-sm transition-all"
        >
          Ver carta y pedir
        </button>
        {sesion && (
          <p className="text-xs text-green-600 font-medium">
            Mesa abierta · {(sesion.comensales || []).length} comensal(es) conectado(s)
          </p>
        )}
      </div>
    </div>
  );

  // ── PANTALLA: Ver cuenta completa de la mesa ──
  if (verCuenta) return (
    <div className="min-h-screen bg-[#FDFCF8]">
      <div className="sticky top-0 bg-white shadow-sm z-10 px-4 py-3 flex items-center justify-between">
        <button onClick={() => setVerCuenta(false)} className="p-2 hover:bg-gray-100 rounded-xl"><X size={20} /></button>
        <h2 className="font-black text-lg">Cuenta Mesa {mesaInfo?.numero}</h2>
        <button onClick={() => setModalDividir(true)} className="flex items-center gap-1 px-3 py-2 bg-blue-50 text-blue-700 rounded-xl text-xs font-bold">
          <SplitSquareVertical size={14} /> Dividir
        </button>
      </div>
      <div className="p-4 space-y-4">
        {(sesion?.pedidos || []).map((p, i) => (
          <div key={i} className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="font-bold text-sm text-gray-900">{p.comensal}</span>
              <span className="text-xs text-gray-400">{p.hora ? new Date(p.hora).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
            </div>
            {(p.items || []).map((it, j) => (
              <div key={j} className="flex justify-between text-sm py-0.5">
                <span className="text-gray-700">{it.cantidad}x {it.nombre}</span>
                <span className="font-semibold">${fmt(it.subtotal)}</span>
              </div>
            ))}
            <div className="border-t mt-2 pt-2 flex justify-between text-sm font-bold">
              <span>Subtotal</span><span className="text-[#D32F2F]">${fmt(p.total)}</span>
            </div>
          </div>
        ))}
        <div className="bg-gray-900 text-white rounded-2xl p-5 text-center">
          <p className="text-sm opacity-70">Total de la mesa</p>
          <p className="text-3xl font-black mt-1">${fmt(sesion?.totalAcumulado || 0)}</p>
        </div>
        {modalDividir && (
          <div className="bg-white rounded-2xl border-2 border-blue-200 p-5 space-y-4">
            <h3 className="font-bold text-gray-900 flex items-center gap-2"><SplitSquareVertical size={18} /> Dividir cuenta</h3>
            <div className="flex items-center justify-center gap-4">
              <button onClick={() => setPartesDividir(Math.max(2, partesDividir - 1))} className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center"><Minus size={18} /></button>
              <span className="text-3xl font-black">{partesDividir}</span>
              <button onClick={() => setPartesDividir(partesDividir + 1)} className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center"><Plus size={18} /></button>
            </div>
            <p className="text-center text-sm text-gray-600">personas</p>
            <div className="bg-blue-50 rounded-xl p-4 text-center">
              <p className="text-xs text-blue-600 font-medium">Cada persona paga</p>
              <p className="text-2xl font-black text-blue-900">${fmt(Math.ceil((sesion?.totalAcumulado || 0) / partesDividir))}</p>
            </div>
            <button onClick={() => setModalDividir(false)} className="w-full py-2 bg-gray-100 rounded-xl text-sm font-bold">Cerrar</button>
          </div>
        )}
      </div>
    </div>
  );

  // ── PANTALLA PRINCIPAL: Carta Digital ──
  return (
    <div className="min-h-screen bg-[#FDFCF8] pb-24">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="px-4 py-3 flex items-center justify-between">
          <div>
            <h1 className="font-black text-lg text-gray-900">{restaurante.nombre}</h1>
            <p className="text-xs text-gray-500">Mesa {mesaInfo?.numero} · {nombreComensal}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setVerCuenta(true)} className="p-2 bg-gray-100 rounded-xl hover:bg-gray-200 relative">
              <Receipt size={20} className="text-gray-700" />
            </button>
          </div>
        </div>
        {/* Búsqueda */}
        <div className="px-4 pb-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input className="w-full pl-9 pr-4 py-2.5 bg-gray-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#D32F2F]/30"
              placeholder="Buscar en la carta..." value={busqueda} onChange={e => setBusqueda(e.target.value)} />
          </div>
        </div>
        {/* Categorías */}
        <div className="px-4 pb-3 flex gap-2 overflow-x-auto scrollbar-hide">
          {categorias.map(c => (
            <button key={c} onClick={() => setCatFiltro(c)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap capitalize transition-all ${catFiltro === c ? 'bg-[#D32F2F] text-white' : 'bg-gray-100 text-gray-600'}`}>
              {c}
            </button>
          ))}
          <button onClick={() => setCatFiltro('')}
            className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all ${!catFiltro ? 'bg-[#D32F2F] text-white' : 'bg-gray-100 text-gray-600'}`}>
            Todo
          </button>
        </div>
      </div>

      {/* Pedido confirmado toast */}
      {pedidoOk && (
        <div className="fixed top-4 inset-x-4 z-50 bg-green-600 text-white rounded-2xl p-4 flex items-center gap-3 shadow-2xl animate-in fade-in slide-in-from-top-3">
          <CheckCircle2 size={24} />
          <div><p className="font-bold">¡Pedido enviado a cocina!</p><p className="text-xs opacity-80">Te avisaremos cuando esté listo.</p></div>
        </div>
      )}

      {/* Grid de productos */}
      {cargando ? (
        <div className="flex items-center justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-[#D32F2F] border-t-transparent rounded-full" /></div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-4">
          {catalogoFiltrado.map(item => {
            const enCarrito = carrito.find(i => i.id === item.id);
            const img = item.foto || item.imagenes?.[0] || item.imagen || (item.fotos && item.fotos[0]);
            const agotado = item.disponible === false;
            // Tamaños, agregados o combo a elegir: se abre el modal, así que se muestra
            // qué trae y cuántos van, sin el −/+ (cada elección es su propia línea)
            const conOpciones = tieneOpciones(item) || esComboElige(item);
            const cantOpciones = conOpciones
              ? carrito.filter(i => i.baseId === item.id).reduce((s, i) => s + i.cantidad, 0) : 0;
            const detalle = item.variantes?.length > 0
              ? item.variantes.map(v => v.nombre).join(' · ')
              : (item.componentes || []).map(c => c.tipo === 'elige' ? `A elegir: ${c.nombre}` : c.nombre).join(' · ');
            return (
              <div key={item.id}
                onClick={conOpciones && !agotado ? () => agregarItem(item) : undefined}
                className={`bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col ${conOpciones && !agotado ? 'cursor-pointer' : ''}`}>
                {img && (
                  <div className={`h-28 bg-gray-100 relative ${agotado ? 'grayscale opacity-50' : ''}`}>
                    <img src={img} alt={item.nombre} className="w-full h-full object-cover" />
                    {agotado && (
                      <span className="absolute inset-0 m-auto h-fit w-fit bg-gray-900/85 text-white text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full">
                        Agotado
                      </span>
                    )}
                  </div>
                )}
                <div className="p-3 flex-1 flex flex-col">
                  <h3 className={`font-bold text-sm line-clamp-2 ${agotado ? 'text-gray-400' : 'text-gray-900'}`}>{item.nombre}</h3>
                  {item.descripcion && <p className="text-[10px] text-gray-500 line-clamp-1 mt-0.5">{item.descripcion}</p>}
                  {detalle && <p className="text-[10px] font-semibold text-indigo-600 line-clamp-2 mt-1">{detalle}</p>}
                  {item.agregados?.length > 0 && <p className="text-[10px] text-emerald-600 font-semibold mt-0.5">+ agregados opcionales</p>}
                  <div className="mt-auto pt-2 flex items-end justify-between">
                    <span className={`font-black text-sm ${agotado ? 'text-gray-400' : 'text-[#D32F2F]'}`}>
                      {item.variantes?.length > 0 && <span className="text-[9px] font-semibold text-gray-400 mr-0.5">desde</span>}
                      ${fmt(precioDesde(item))}
                    </span>
                    {agotado ? (
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Agotado</span>
                    ) : conOpciones ? (
                      <button onClick={e => { e.stopPropagation(); agregarItem(item); }} className="relative w-8 h-8 bg-[#D32F2F] text-white rounded-full flex items-center justify-center hover:bg-[#B71C1C] transition-all active:scale-90">
                        <Plus size={16} />
                        {cantOpciones > 0 && (
                          <span className="absolute -top-1.5 -right-1.5 min-w-4 h-4 px-1 bg-gray-900 text-white text-[9px] font-black rounded-full flex items-center justify-center">{cantOpciones}</span>
                        )}
                      </button>
                    ) : enCarrito ? (
                      <div className="flex items-center gap-1 bg-gray-100 rounded-full px-1 py-0.5">
                        <button onClick={() => restarItem(item.id)} className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-white"><Minus size={12} /></button>
                        <span className="text-xs font-black w-4 text-center">{enCarrito.cantidad}</span>
                        <button onClick={() => agregarItem(item)} className="w-6 h-6 flex items-center justify-center rounded-full bg-[#D32F2F] text-white"><Plus size={12} /></button>
                      </div>
                    ) : (
                      <button onClick={() => agregarItem(item)} className="w-8 h-8 bg-[#D32F2F] text-white rounded-full flex items-center justify-center hover:bg-[#B71C1C] transition-all active:scale-90">
                        <Plus size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Botón flotante carrito */}
      {cantCarrito > 0 && (
        <div className="fixed bottom-5 inset-x-4 flex justify-center z-20">
          <button onClick={() => setDrawerAbierto(true)}
            className="bg-[#D32F2F] hover:bg-[#B71C1C] text-white px-5 py-4 rounded-2xl shadow-2xl flex items-center justify-between gap-3 font-bold text-sm transition-all active:scale-95 max-w-sm w-full">
            <div className="flex items-center gap-3">
              <div className="bg-white/25 rounded-xl px-2.5 py-1 font-black">{cantCarrito}</div>
              <span>Ver pedido</span>
            </div>
            <span className="font-black">${fmt(totalCarrito)}</span>
          </button>
        </div>
      )}

      {/* Drawer del carrito */}
      {drawerAbierto && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDrawerAbierto(false)} />
          <div className="relative bg-[#FDFCF8] w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl max-h-[85vh] flex flex-col shadow-2xl overflow-hidden">
            <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mt-3 mb-1 sm:hidden shrink-0" />
            <div className="px-5 py-4 border-b flex items-center justify-between shrink-0 bg-white">
              <h3 className="font-black text-xl">Tu Pedido</h3>
              <button onClick={() => setDrawerAbierto(false)} className="p-2 rounded-full hover:bg-gray-100"><X size={20} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {carrito.map(item => (
                <div key={item.id} className="flex items-center gap-3">
                  <div className="flex items-center gap-1 bg-gray-50 rounded-xl p-1 shrink-0 border">
                    <button onClick={() => restarItem(item.id)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white"><Minus size={14} /></button>
                    <span className="w-6 text-center text-sm font-black">{item.cantidad}</span>
                    <button onClick={() => agregarItem(item)} className="w-8 h-8 flex items-center justify-center rounded-lg text-[#D32F2F]"><Plus size={14} /></button>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold truncate">{item.nombre}</p>
                    <p className="text-xs text-[#D32F2F] font-semibold">${fmt(item.subtotal)}</p>
                  </div>
                  <button onClick={() => setCarrito(prev => prev.filter(i => i.id !== item.id))} className="p-2 text-gray-300 hover:text-red-500"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
            <div className="px-5 py-4 border-t bg-white space-y-3 shrink-0">
              <div className="flex justify-between items-center">
                <span className="font-bold">Total</span>
                <span className="text-xl font-black text-[#D32F2F]">${fmt(totalCarrito)}</span>
              </div>
              <button onClick={enviarPedido} disabled={enviando || carrito.length === 0}
                className="w-full py-4 bg-[#D32F2F] hover:bg-[#B71C1C] disabled:bg-gray-300 text-white rounded-2xl font-black text-base transition-all flex items-center justify-center gap-2">
                {enviando ? <><div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" /> Enviando...</>
                  : <><ChefHat size={20} /> Enviar a cocina</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de combo: elegir componentes */}
      {comboEligiendo && (
        <ModalEligeCombo
          combo={comboEligiendo}
          onConfirmar={confirmarComboElige}
          onCerrar={() => setComboEligiendo(null)}
        />
      )}

      {/* Modal de tamaños y agregados */}
      {itemOpciones && (
        <ModalOpciones
          item={itemOpciones}
          onConfirmar={confirmarOpciones}
          onCerrar={() => setItemOpciones(null)}
        />
      )}
    </div>
  );
}
